import { FC, useEffect, useMemo, useRef, useState } from "react";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";

import { IIdleStatusResponse } from "../../index";
import { IStationScheme, IStationSchemeObject } from "../../interfaces";
import { createNoCacheHeaders } from "../../no-cache-headers";
import { SchemeArea } from "./components/SchemeArea";
import { useAsu, RangeType } from "@framework/provider-asu";
import { mergeRecorderModeStationObjects } from "./recorder/mergeSeries";
import { useSubscribe } from "../../../../../../../subscribe/provider";
import { SubscribeCode, SubscribeMessage } from "../../../../../../../subscribe/types";

export interface IStationSchemeImageView {
  stationScheme: IStationScheme[];
  idleStatuses: IIdleStatusResponse[];
  flashUnacknowledgedAlarms: boolean;
  flashFaultAlarms: boolean;
}

export const StationSchemeImageView: FC<IStationSchemeImageView> = props => {
  const { stationScheme, idleStatuses, flashUnacknowledgedAlarms, flashFaultAlarms } = props;
  const scheme = stationScheme[0];
  const schemeId = scheme?.idScheme;

  const { startTimestamp, endTimestamp, userSelectedRangeType, recorderWindowStart, isRecorderModeActive, dbTz } =
    useAsu();

  const [objects, setObjects] = useState<IStationSchemeObject[]>([]);
  const { connected: subscribeConnected, enabled: subscribeModeEnabled, subscribe } = useSubscribe();
  const subscribeModeActive = subscribeModeEnabled && subscribeConnected;
  const subscribeBootstrapSchemeIdRef = useRef<number | null>(null);

  const { fn: getStationObjects, isLoading } = useApiCall(
    async (api, data: { idScheme: number; startTimestamp: string | Date; endTimestamp: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: "/stations/scheme-objects",
          data,
          headers: createNoCacheHeaders(`station-scheme-objects-${data.idScheme}`),
        })) as IStationSchemeObject[] | null;

        return json ?? [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    { success: false, error: false },
  );

  const fetchObjects = () => {
    if (!schemeId) return;
    if (userSelectedRangeType === RangeType.RECORDER_MODE && (!isRecorderModeActive || !recorderWindowStart)) return;

    void getStationObjects(undefined, {
      idScheme: schemeId,
      startTimestamp,
      endTimestamp,
    }).then(json => {
      const newObjectsArray = Array.isArray(json) ? json : [];
      if (userSelectedRangeType === RangeType.RECORDER_MODE) {
        setObjects(prev => mergeRecorderModeStationObjects(prev, newObjectsArray, recorderWindowStart, dbTz));
      } else {
        setObjects(newObjectsArray);
      }
    });
  };

  useEffect(() => {
    if (subscribeModeActive && subscribeBootstrapSchemeIdRef.current === schemeId) {
      return;
    }
    if (subscribeModeActive && schemeId) {
      subscribeBootstrapSchemeIdRef.current = schemeId;
    }
    fetchObjects();
  }, [schemeId, endTimestamp, subscribeModeActive]);

  useEffect(() => {
    subscribeBootstrapSchemeIdRef.current = null;
    setObjects([]);
  }, [schemeId]);

  const subscriptionRequestsKey = useMemo(
    () =>
      Array.from(
        new Set(
          objects.flatMap(object => {
            const id = Number(object.SchemeIdBindObject);
            if (!Number.isFinite(id) || id <= 0) return [];
            if (object.SchemeBindObjectType === 1) return [`${SubscribeCode.STATUS_TOPLEVEL}:${id}`];
            if (object.SchemeBindObjectType === 2) {
              return [`${SubscribeCode.STATUS_TRAIN}:${id}`, `${SubscribeCode.PARAM}:${id}`];
            }
            if (object.SchemeBindObjectType === 3) return [`${SubscribeCode.STATUS_POINT}:${id}`];
            if (object.SchemeBindObjectType === 4) {
              return [`${SubscribeCode.SCALAR}:${id}`, `${SubscribeCode.DYNAMIC_DATA}:${id}`];
            }
            return [];
          }),
        ),
      )
        .sort()
        .join(","),
    [objects],
  );

  useEffect(() => {
    if (!subscribeModeActive || !subscriptionRequestsKey) {
      return;
    }

    const handleMessages = (messages: SubscribeMessage[]): void => {
      if (!messages.length) {
        return;
      }
      setObjects(previous =>
        previous.map(object => {
          const id = Number(object.SchemeIdBindObject);
          const relevant = messages.filter(message => {
            if ("idMeasure" in message) return object.SchemeBindObjectType === 4 && message.idMeasure === id;
            if ("ObjectId" in message) return message.ObjectId === id;
            return object.SchemeBindObjectType === 2 && "idTrain" in message && message.idTrain === id;
          });
          const message = relevant[relevant.length - 1];
          if (!message) return object;
          if ("idMeasure" in message) {
            return {
              ...object,
              value: message.Value1 === null ? null : String(message.Value1),
              rawValue: message.Value1,
              ldd: message.Date,
              alarmStatus: message.AlarmStatus,
            };
          }
          if ("ObjectId" in message) {
            return { ...object, ldd: message.Date, alarmStatus: message.AlarmStatus };
          }
          if ("Value" in message && message.Value !== undefined) {
            return { ...object, value: String(message.Value), rawValue: message.Value, ldd: message.Date };
          }
          return { ...object, ldd: message.Date };
        }),
      );
    };

    const cleanups = subscriptionRequestsKey.split(",").map(value => {
      const [type, id] = value.split(":").map(Number);
      return subscribe({ type: type as SubscribeCode, id }, handleMessages);
    });
    return () => cleanups.forEach(cleanup => cleanup());
  }, [subscribe, subscribeModeActive, subscriptionRequestsKey]);

  const schemeObjects = idleStatuses.length
    ? objects.map(obj => {
        const status = idleStatuses.find(status => status.id === obj.SchemeIdBindObject);
        return { ...obj, idleStatus: !!status };
      })
    : objects;

  return (
    <ProgressOverlay isLoading={isLoading}>
      <SchemeArea
        scheme={scheme}
        objects={schemeObjects}
        flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
        flashFaultAlarms={flashFaultAlarms}
      />
    </ProgressOverlay>
  );
};
